import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiConfig, getApiUrl } from 'src/config/apiConfig';
import axiosIn from 'src/middlewares/axiosInterceptor';
import { extractErrorMsg } from 'src/utils/utils';
import { SecurityActionPlanType } from 'src/utils/enums';

export const initial_formAddNewDomain = { url: '', form_type: 'CREATE', scanPackageName_: 'Basic', excludedScans: [] };

// Helper function to convert SecurityActionPlanType enum to key string
const getActionPlanKey = (actionPlanType: number): string | null => {
  switch (actionPlanType) {
    case SecurityActionPlanType.Dns:
      return 'Dns';
    case SecurityActionPlanType.Header:
      return 'Headers';
    case SecurityActionPlanType.Cve:
      return 'Vulnerabilities';
    case SecurityActionPlanType.Port:
      return 'Ports';
    case SecurityActionPlanType.Ssl:
      return 'Certificates';
    default:
      return null;
  }
};

const initialState = {
  toastMessage: { show: false, color: '', iconKey: '', msg: '', subtitle: '', duration: 3000, autoClose: true },
  showScanDomainDialog: false, // or editObj
  breadcrumb: [
    {
      to: '/',
      title: 'Home',
    },
  ],

  persist: {
    // is_initial_fetches_loading: true,
    // is_active_domain_on_scan: false,
    // userJobScheduleId: '', // this is not domainId but Apis are working with it, its from the schedule-scan
    // active_domain_data: {},
    // users_me_profile: [],
    // domains: [],
    schedule_scan: undefined, // Changed from [] to undefined to indicate not yet fetched
    // scan_type_packages: [],
    // -----domain-specifics---
    // report_headers: [],
    // report_ports: [],
    // report_requests: [],
    // report_subdomains: [],
    // report_technologies: [],
    // report_vulnerabilities: [],
    // report_dns_security: [],
    // report_ssl_securities: [], // ornekdata_certificates
    // report_secret_apis: [],
    // report_malicious: [],
    // report_sensitive: [],
    // report_wafs: [],
    // report_subdomain_takeovers: [],
    // llm_report: [],
    // notifications: [],
    // summary_headers: [],
    // summary_ports: [],
    // summary_subdomains: [],
    // summary_vulnerabilities: [],
    // chart_headers: [],
    // chart_ports: [],
    // chart_subdomains: [],
    // chart_vulnerabilities: [],
    // activeDomainStatus,
    // scanStatistics: [],
    action_plans: {
      Dns: null,
      Headers: null,
      Vulnerabilities: null,
      Ports: null,
      Certificates: null,
    },
  },
  isLoadings: {
    // report_vulnerabilities: false,
  },
  sideMenuExpandedTitles: JSON.parse(window.sessionStorage.getItem('sidemenuexpandeds') || '[]'),
  allSearchTexts: {
    targets: '',
  },
  open_search_menu: false,
  actionButtonStates: {
    subdomains: {
      history: {
        open: false,
        selectedDate: null,
        fetchedDates: null,
      },
    },
  },
  selectedHistory: null,
  // {
  //   date: null, // 2025-12-30
  //   hour: null, // 23:00
  //   websiteVulnerabilityId: null, // 123456
  //   isLatest: false,
  // },
  secuseekGenerateApiKeyDialog: { show: false, cannotClose: false, existingData: null },
  chatbot: { isOpen: false, websiteVulnerabilityId: '', findingId: '' },
};

export const appSlice: any = createSlice({
  name: 'appSlice',
  initialState,
  reducers: {
    setToastMessage: (state, action: { payload: SetToastMessagePayload }) => {
      if (typeof action.payload === 'string') {
        state.toastMessage = {
          show: true,
          msg: action.payload,
          subtitle: '',
          color: 'warning',
          iconKey: 'warning',
          duration: 3000,
          autoClose: true,
        };
      } else {
        const iconKeyMap = {
          success: 'check_circle',
          error: 'error',
          warning: 'warning',
          info: 'info',
        };
        state.toastMessage = {
          ...state.toastMessage,
          ...action.payload,
          subtitle: action.payload.subtitle !== undefined ? action.payload.subtitle : '',
          iconKey: action.payload.iconKey || iconKeyMap[action.payload.color || 'warning'],
          // Respect show value from payload if provided, otherwise default to true for new messages
          show: action.payload.show !== undefined ? action.payload.show : (action.payload.msg ? true : state.toastMessage.show),
          // Ensure autoClose defaults to true and duration defaults to 3000 if not explicitly provided
          autoClose: action.payload.autoClose !== undefined ? action.payload.autoClose : true,
          duration: action.payload.duration !== undefined ? action.payload.duration : (state.toastMessage.duration || 3000),
        };
      }
    },
    setBreadcrumb: (state, action) => {
      state.breadcrumb = action.payload;
    },
    setShowScanDomainDialog: (state, action: any) => {
      const newState = action.payload === true ? initial_formAddNewDomain : !action.payload ? false : { ...initial_formAddNewDomain, ...action.payload };
      state.showScanDomainDialog = action.payload === undefined ? false : newState;
    },
    setSelectedHistory: (state, action: any) => {
      state.selectedHistory = action.payload;
    },
    setSecuseekGenerateApiKeyDialog: (state, action: any) => {
      state.secuseekGenerateApiKeyDialog = { ...state.secuseekGenerateApiKeyDialog, ...action.payload };
    },
    setPersist: (state, action) => {
      const { key, value } = action.payload;
      state.persist[key] = value;
    },
    clearPersistForSelectedDomain: (state: any, action) => {
      // Only non-domain-specific ones are left:
      const kept = {
        is_initial_fetches_loading: state.persist.is_initial_fetches_loading,
        is_active_domain_on_scan: state.persist.is_active_domain_on_scan,
        realtime_connected: state.persist.realtime_connected,
        userJobScheduleId: state.persist.userJobScheduleId,
        users_me_profile: state.persist.users_me_profile,
        domains: state.persist.domains,
        schedule_scan: state.persist.schedule_scan,
        scan_type_packages: state.persist.scan_type_packages,
        action_plans: {
          Dns: null,
          Headers: null,
          Vulnerabilities: null,
          Ports: null,
          Certificates: null,
        },
      };
      state.persist = kept;
    },
    setAllSearchTexts: (state, action) => {
      const { key, value } = action.payload;
      state.allSearchTexts[key] = value;
    },
    setIsLoadings: (state, action) => {
      const { key, value } = action.payload;
      state.isLoadings[key] = value;
    },
    setOpen_search_menu: (state, action) => {
      state.open_search_menu = !!action.payload;
    },
    setActionButtonStates: (state, action) => {
      const { buttonName, tableName, colName, value } = action.payload;
      if (tableName && buttonName && colName) {
        state.actionButtonStates[tableName][buttonName][colName] = value;
      } else if (tableName && buttonName) {
        state.actionButtonStates[tableName][buttonName] = value;
      } else if (tableName) {
        state.actionButtonStates[tableName] = value;
      } else {
        state.actionButtonStates = value;
      }
    },
    setChatbotOpen: (state, action) => {
      state.chatbot.isOpen = true;
      if (action.payload) {
        state.chatbot.websiteVulnerabilityId = action.payload.websiteVulnerabilityId || '';
        state.chatbot.findingId = action.payload.findingId || '';
      }
    },
    setChatbotClose: (state) => {
      state.chatbot.isOpen = false;
    },
    clearActionPlan: (state: any, action: { payload: { pageKey: string } }) => {
      const { pageKey } = action.payload;
      if (pageKey && state.persist.action_plans[pageKey]) {
        state.persist.action_plans[pageKey] = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getScanStatus.fulfilled, (state: any, action: any) => {
      const { data, forActiveDomain } = action?.payload || {};
      // if (data) state.persist.activeDomainStatus = data;
      if (data) state.persist.active_domain_data = { ...state.persist.active_domain_data, status: data };
      if (forActiveDomain) {
        if (data?.isCompleted !== true) {
          state.persist.is_active_domain_on_scan = true; // This is the line 136
        } else {
          state.persist.is_active_domain_on_scan = false;
        }
      }
    });
    builder.addCase(getScanStatistics.fulfilled, (state: any, action: any) => {
      if (!action?.payload) return;
      const { data } = action.payload;
      if (data) state.persist.scanStatistics = data;
    });
    builder.addCase(deleteScheduleScan.fulfilled, (state: any, action: any) => {
      const { status } = action?.payload || {};
      if (status === 200) {
        // Get the scanId from the action meta
        const scanId = action.meta.arg;
        if (scanId && Array.isArray(state.persist.schedule_scan)) {
          // Remove the deleted target from schedule_scan list
          state.persist.schedule_scan = state.persist.schedule_scan.filter((item: any) => item.id !== scanId);
        }
      }
    });
    builder.addCase(setSideMenuExpandedTitles.fulfilled, (state, action) => {
      const { title, multiExpandSideMenu } = action?.payload || {};

      if (multiExpandSideMenu === false) {
        if (state.sideMenuExpandedTitles.includes(title)) {
          state.sideMenuExpandedTitles = [];
        } else {
          state.sideMenuExpandedTitles = [title];
        }
        window?.sessionStorage?.setItem('sidemenuexpandeds', JSON.stringify(state.sideMenuExpandedTitles));
      } else {
        if (state.sideMenuExpandedTitles.includes(title)) {
          state.sideMenuExpandedTitles = state.sideMenuExpandedTitles.filter((item) => item !== title);
        } else {
          state.sideMenuExpandedTitles.push(title);
        }
        window?.sessionStorage?.setItem('sidemenuexpandeds', JSON.stringify(state.sideMenuExpandedTitles));
      }
    });
    builder.addCase(getActionPlan.fulfilled, (state: any, action: any) => {
      if (action?.payload?.data?.actionPlan) {
        const actionPlanType = action.meta.arg.actionPlanType;
        const key = getActionPlanKey(actionPlanType);
        if (key) {
          state.persist.action_plans[key] = action.payload.data.actionPlan;
        }
      }
    });
    builder.addCase(createActionPlan.fulfilled, (state: any, action: any) => {
      if (action?.payload?.data?.actionPlan) {
        const actionPlanType = action.meta.arg.actionPlanType;
        const key = getActionPlanKey(actionPlanType);
        if (key) {
          state.persist.action_plans[key] = action.payload.data.actionPlan;
        }
      }
    });
    builder.addCase(retryActionPlan.fulfilled, (state: any, action: any) => {
      if (action?.payload?.data?.actionPlan) {
        const actionPlanType = action.meta.arg.actionPlanType;
        const key = getActionPlanKey(actionPlanType);
        if (key) {
          state.persist.action_plans[key] = action.payload.data.actionPlan;
        }
      }
    });
    builder.addCase(solveActionPlanStep.fulfilled, (state: any, action: any) => {
      if (action?.payload?.status === 200) {
        const actionPlanType = action.meta.arg.actionPlanType;
        const key = getActionPlanKey(actionPlanType);
        if (key) {
          // If response includes updated actionPlan, use it; otherwise update step locally
          if (action.payload.data?.actionPlan) {
            state.persist.action_plans[key] = action.payload.data.actionPlan;
          } else if (state.persist.action_plans[key]?.steps) {
            const stepId = action.payload.stepId || action.meta.arg.stepId;
            const step = state.persist.action_plans[key].steps.find((s: any) => s.id === stepId);
            if (step) {
              step.isSolved = true;
            }
          }
        }
      }
    });
    builder.addCase(unsolveActionPlanStep.fulfilled, (state: any, action: any) => {
      if (action?.payload?.status === 200) {
        const actionPlanType = action.meta.arg.actionPlanType;
        const key = getActionPlanKey(actionPlanType);
        if (key) {
          // If response includes updated actionPlan, use it; otherwise update step locally
          if (action.payload.data?.actionPlan) {
            state.persist.action_plans[key] = action.payload.data.actionPlan;
          } else if (state.persist.action_plans[key]?.steps) {
            const stepId = action.payload.stepId || action.meta.arg.stepId;
            const step = state.persist.action_plans[key].steps.find((s: any) => s.id === stepId);
            if (step) {
              step.isSolved = false;
            }
          }
        }
      }
    });
  },
});

//* ------------------Sidemenu---------------------
export const setSideMenuExpandedTitles: any = createAsyncThunk('appSlice/setSideMenuExpandedTitles', async (title: string, { getState }) => {
  const state = getState() as any;
  const customizer = state.customizer;
  return { title, multiExpandSideMenu: customizer?.multiExpandSideMenu };
});
//* ------------------Scan---------------------
export const postScheduleScan: any = createAsyncThunk<any, any>('appSlice', async (data, { getState }) => {
  const curl = getApiUrl(apiConfig.schedule_scan);
  try {
    const response = await axiosIn.post(curl, data);
    return { data: response.data, status: response.status };
  } catch (error: any) {
    // const errorMessage = extractErrorMsg(error.response?.data?.errors || error.response?.data?.message || '');
    const errorMessage = extractErrorMsg(error || '');
    return { errMsg: errorMessage, status: error.response.status };
  }
});
export const deleteScheduleScan: any = createAsyncThunk<any, string>('appSlice', async (scanId, { getState }) => {
  const state = getState() as any;
  const id = scanId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!id) return { data: null, status: 400, errMsg: 'Scan ID is required (ui)' };
  const curl = getApiUrl(apiConfig.scheduled_scan, { id: id });
  const response = await axiosIn.delete(curl);
  return { data: response.data, status: response.status };
});
export const updateScheduleScan: any = createAsyncThunk<any, { scanId: string; data: any }>('appSlice', async ({ scanId, data }, { getState }) => {
  const state = getState() as any;
  const id = scanId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!id) return { data: null, status: 400, errMsg: 'Scan ID is required (ui)' };
  const curl = getApiUrl(apiConfig.scheduled_scan, { id: id });
  const response = await axiosIn.put(curl, data);
  return { data: response.data, status: response.status };
});
export const startManualScan = createAsyncThunk<any, { scanId: number | string }>('appSlice', async ({ scanId }, { getState }) => {
  const curl = getApiUrl(apiConfig.start_manual_scan, { id: scanId });
  try {
    const response = await axiosIn.put(curl, { id: scanId });
    return { data: response.data, status: response.status };
  } catch (error: any) {
    // Axios errors have a response object
    const status = error.response?.status ?? 500;
    // const errorMessage = extractErrorMsg(error.response?.data?.errors || error.response?.data?.message || '');
    const errorMessage = extractErrorMsg(error);
    return { errMsg: errorMessage, status };
  }
});
export const resumeScan = createAsyncThunk<any, { scanId: string }>('appSlice', async ({ scanId }, { getState }) => {
  // const state = getState() as any;
  // const id = scanId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!scanId) return { data: null, status: 400, errMsg: 'Scan ID is required (ui)' };
  const curl = getApiUrl(apiConfig.resume_scan, { id: scanId });
  try {
    const response = await axiosIn.put(curl, { id: scanId });
    return { data: response.data, status: response.status };
  } catch (error: any) {
    const status = error.response?.status ?? 500;
    const errorMessage = extractErrorMsg(error);
    return { errMsg: errorMessage, status };
  }
});
export const pauseScan = createAsyncThunk<any, { scanId: number | string }>('appSlice', async ({ scanId }, { getState }) => {
  if (!scanId) return { data: null, status: 400, errMsg: 'Scan ID is required (ui)' };
  const curl = getApiUrl(apiConfig.pause_scan, { id: scanId });
  try {
    const response = await axiosIn.put(curl, { id: scanId });
    return { data: response.data, status: response.status };
  } catch (error: any) {
    const status = error.response?.status ?? 500;
    const errorMessage = extractErrorMsg(error);
    return { errMsg: errorMessage, status };
  }
});
export const getScanStatus: any = createAsyncThunk<any, { scanId?: string }>('appSlice.persist.activeDomainStatus', async ({ scanId }, { getState }) => {
  const state = getState() as any;
  const id = scanId || state.appGeneralReducer.selectedHistory?.websiteVulnerabilityId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!id) return { data: null, status: 400, forActiveDomain: false };
  const curl = getApiUrl(apiConfig.scan_status, { id: id });
  const response = await axiosIn.get(curl);
  return { data: response.data, status: response.status, forActiveDomain: !id || id === state.appGeneralReducer.persist.userJobScheduleId };
});
export const getScanStatistics: any = createAsyncThunk<any, { scanId?: string }>('appSlice.persist.scanStatistics', async ({ scanId }, { getState }) => {
  const state = getState() as any;
  const id = scanId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!id) return { data: null, status: 400 };
  const curl = getApiUrl(apiConfig.scan_statistics, { id: id });
  const response = await axiosIn.get(curl);
  return { data: response.data, status: response.status };
});
export const fetch_history_of_a_scan: any = createAsyncThunk<any, { userJobScheduleId?: string, scanName?: string, body?: any }>('appSlice.persist.fetch_history_of_a_scan', async ({ userJobScheduleId, scanName, body }, { getState }) => {
  const state = getState() as any;
  try {
    const curl = getApiUrl(apiConfig.history_of_a_scan, { userJobScheduleId: state.appGeneralReducer.persist.active_domain_data.id, scanName: scanName });
    const response = await axiosIn.get(curl + `?year=${body.year}&month=${body.month}`);
    return { data: response.data, status: response.status };
  } catch (error: any) {
    console.error('error', error)
    const errorMessage = extractErrorMsg(error);
    return { errMsg: error.message || errorMessage, status: error?.response?.status || 500 };
  }
});

//* ------------------Action Plan--------------------- 
export const getActionPlan: any = createAsyncThunk<any, { actionPlanType: number, websiteVulnerabilityId?: string }>('appSlice.persist.getActionPlan', async ({ actionPlanType, websiteVulnerabilityId }, { getState }) => {
  const state = getState() as any;
  // Use provided websiteVulnerabilityId or fallback to state
  const id = websiteVulnerabilityId || state.appGeneralReducer.persist.active_domain_data?.lastWebsiteVulnerabilityId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!id) return { data: null, status: 400, errMsg: 'websiteVulnerabilityId is required' };

  try {
    const curl = getApiUrl(apiConfig.llm_action_plan_get, { websiteVulnerabilityId: id });
    const response = await axiosIn.get(curl + `?actionPlanType=${actionPlanType}`);
    return { data: response.data, status: response.status };
  } catch (error: any) {
    const errorMessage = extractErrorMsg(error);
    return { errMsg: error.message || errorMessage, status: error?.response?.status || 500 };
  }
});

export const createActionPlan: any = createAsyncThunk<any, { actionPlanType: number }>('appSlice.persist.createActionPlan', async ({ actionPlanType }, { getState, rejectWithValue }) => {
  const state = getState() as any;
  const websiteVulnerabilityId = state.appGeneralReducer.persist.active_domain_data?.lastWebsiteVulnerabilityId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!websiteVulnerabilityId) return rejectWithValue({ errMsg: 'websiteVulnerabilityId is required', status: 400 });

  try {
    const curl = getApiUrl(apiConfig.llm_action_plan_post, { websiteVulnerabilityId });
    const response = await axiosIn.post(curl, { actionPlanType });
    return { data: response.data, status: response.status };
  } catch (error: any) {
    const errorMessage = extractErrorMsg(error);
    return rejectWithValue({ errMsg: errorMessage || error.message || 'Failed to create action plan', status: error?.response?.status || 500 });
  }
});

export const retryActionPlan: any = createAsyncThunk<any, { actionPlanType: number }>('appSlice.persist.retryActionPlan', async ({ actionPlanType }, { getState }) => {
  const state = getState() as any;
  const websiteVulnerabilityId = state.appGeneralReducer.persist.active_domain_data?.lastWebsiteVulnerabilityId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!websiteVulnerabilityId) return { data: null, status: 400, errMsg: 'websiteVulnerabilityId is required' };

  try {
    const curl = getApiUrl(apiConfig.llm_action_plan_retry, { websiteVulnerabilityId });
    const response = await axiosIn.post(curl, { actionPlanType });
    return { data: response.data, status: response.status };
  } catch (error: any) {
    const errorMessage = extractErrorMsg(error);
    return { errMsg: error.message || errorMessage, status: error?.response?.status || 500 };
  }
});

export const solveActionPlanStep: any = createAsyncThunk<any, { actionPlanType: number, stepId: string }>('appSlice.persist.solveActionPlanStep', async ({ actionPlanType, stepId }, { getState }) => {
  const state = getState() as any;
  const websiteVulnerabilityId = state.appGeneralReducer.persist.active_domain_data?.lastWebsiteVulnerabilityId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!websiteVulnerabilityId) return { data: null, status: 400, errMsg: 'websiteVulnerabilityId is required' };

  try {
    const curl = getApiUrl(apiConfig.llm_action_plan_solve_step, { websiteVulnerabilityId });
    const response = await axiosIn.put(curl, { actionPlanType, stepId });
    return { data: response.data, status: response.status, stepId, isSolved: true };
  } catch (error: any) {
    const errorMessage = extractErrorMsg(error);
    // Check if error indicates step is already solved
    const errorData = error?.response?.data;
    if (Array.isArray(errorData) && errorData.length > 0) {
      const firstError = errorData[0];
      if (firstError?.message && firstError.message.includes('already solved')) {
        // Return success-like response to update UI
        return { data: null, status: 200, stepId, isSolved: true, alreadySolved: true };
      }
    }
    return { errMsg: error.message || errorMessage, status: error?.response?.status || 500, stepId };
  }
});

export const unsolveActionPlanStep: any = createAsyncThunk<any, { actionPlanType: number, stepId: string }>('appSlice.persist.unsolveActionPlanStep', async ({ actionPlanType, stepId }, { getState }) => {
  const state = getState() as any;
  const websiteVulnerabilityId = state.appGeneralReducer.persist.active_domain_data?.lastWebsiteVulnerabilityId || state.appGeneralReducer.persist.userJobScheduleId;
  if (!websiteVulnerabilityId) return { data: null, status: 400, errMsg: 'websiteVulnerabilityId is required' };

  try {
    const curl = getApiUrl(apiConfig.llm_action_plan_unsolve_step, { websiteVulnerabilityId });
    const response = await axiosIn.put(curl, { actionPlanType, stepId });
    return { data: response.data, status: response.status, stepId, isSolved: false };
  } catch (error: any) {
    const errorMessage = extractErrorMsg(error);
    // Check if error indicates step is already in desired state
    const errorData = error?.response?.data;
    if (Array.isArray(errorData) && errorData.length > 0) {
      const firstError = errorData[0];
      const errorMsg = firstError?.message?.toLowerCase() || '';
      // Check for "already unsolved" or similar patterns
      if (errorMsg.includes('already unsolved') || (errorMsg.includes('already') && errorMsg.includes('unsolved'))) {
        // Return success-like response to update UI
        return { data: null, status: 200, stepId, isSolved: false, alreadyUnsolved: true };
      }
    }
    return { errMsg: error.message || errorMessage, status: error?.response?.status || 500, stepId };
  }
});

export const {
  setToastMessage, //
  setBreadcrumb,
  setShowScanDomainDialog,
  setSecuseekGenerateApiKeyDialog,
  setSelectedHistory,
  setPersist,
  clearPersistForSelectedDomain,
  setAllSearchTexts,
  setIsLoadings,
  setOpen_search_menu,
  setActionButtonStates,
  setChatbotOpen,
  setChatbotClose,
  clearActionPlan
} = appSlice.actions;

export default appSlice.reducer;

interface ToastMessage {
  show?: boolean;
  color?: string;
  iconKey?: string;
  msg?: string;
  duration?: number;
  autoClose?: boolean;
  subtitle?: string;
}

type SetToastMessagePayload = string | Partial<ToastMessage>;
